import {
  Component,
  OnInit,
  Renderer2,
  ElementRef,
  OnDestroy,
  ViewChild,
  Input
} from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { forkJoin, Observable, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ModalComponent } from '../modal/modal.component';
import { VideoSuggestion } from './video-suggestions.model';
import { VideoSuggestionsService } from './video-suggestions.service';
import {
  AssetLoaderService,
  AssetModel,
  AssetType
} from '../../services/asset-loader/asset-loader.service';
import { ConfigurationService, IAppSettings } from '../../services/configuration/configuration.service';

@Component({
  selector: 'oir-video-suggestions',
  templateUrl: './video-suggestions.component.html',
  styleUrls: ['./video-suggestions.component.less']
})
export class VideoSuggestionsComponent implements OnInit, OnDestroy {

  @Input('lang') lang: string;

  @ViewChild('modal') modal: ModalComponent;

  private listener: () => void;
  private player: amp.Player;

  public model$: Subject<VideoSuggestion> = new Subject<VideoSuggestion>();
  public moduleName$ = new Observable<string>();
  public showHelp = false;
  public videoSuggestions$: Observable<Array<VideoSuggestion>>;
  public videoSuggestions: Array<VideoSuggestion> = [];
  public filter$: Subject<string> = new Subject();

  public model: VideoSuggestion;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private router: Router,
    private videoSuggestionService: VideoSuggestionsService,
    private assetLoaderService: AssetLoaderService,
    private configuration: ConfigurationService<IAppSettings>
  ) {}

  ngOnInit() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.showHelp = false;
      }

      if (event instanceof NavigationEnd) {
        this.getVideos();
      }
    });

    this.model$.subscribe(video => {
      this.model = video;

      if (!this.modal.visible) {
        this.modal.toggle();
        this.showHelp = false;
      }

      this.playVideo();
    });

    this.filter$.subscribe(value => {
      this.videoSuggestions.forEach(
        x =>
          (x.hidden =
            x.description.toLowerCase().indexOf(value.toLowerCase()) == -1)
      );
    });

    const ampJs: AssetModel = {
      src: '//amp.azure.net/libs/amp/2.1.5/azuremediaplayer.min.js',
      type: AssetType.Script,
      loaded: false
    };
    const ampCss: AssetModel = {
      src: '//amp.azure.net/libs/amp/2.1.5/skins/amp-default/azuremediaplayer.min.css',
      type: AssetType.StyleSheet,
      loaded: false
    };

    const scripts$ = this.assetLoaderService.load(ampJs);
    const styles$ = this.assetLoaderService.load(ampCss);

    forkJoin(scripts$, styles$).subscribe(() => {
      this.initPlayer();
    });
  }

  ngOnDestroy() {
    if (this.listener) this.listener();
  }

  private initPlayer() {
    const options = {
      controls: true,
      width: '689',
      height: '400'
    };

    this.player = amp('player', options);
    this.player.src([{ src: '', type: 'video/mp4' }]);
  }

  private registerListener() {
    this.listener = this.renderer.listen('document', 'click', (event: any) => {
      if (!this.el.nativeElement.contains(event.target)) {
        this.showHelp = false;

        this.listener();
      }
    });
  }

  private playVideo() {
    this.player.src([{ src: (this.lang === 'ga' ? this.model.irishSourceUrl : this.model.sourceUrl), type: 'video/mp4' }]);

    setTimeout(() => this.player.play(), 1000);
  }

  public getVideos() {
    this.moduleName$ = this.configuration.get('ModuleName');
    this.videoSuggestions$ = this.moduleName$.pipe(
      switchMap(moduleName => this.videoSuggestionService
          .getVideos(this.router.url, moduleName)
          .pipe(map(response => (this.videoSuggestions = response)))
      )
    );
  }

  public closeVideo() {
    this.player.pause();
    this.modal.close();

    this.model = undefined;
  }

  public onModalClose(): void {
    this.player.pause();
  }

  public toggleHelp() {
    this.showHelp = !this.showHelp;

    if (this.showHelp) {
      this.registerListener();
    } else {
      if (this.listener) this.listener();
    }
  }

  public viewAll(): void {
    if (!this.modal.visible) {
      this.modal.toggle();
      this.player.pause();
    }
  }
}
